import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, CheckCircle, AlertTriangle, Shield,
  UserPlus, UserMinus, ArrowLeftRight, Key, Lock, Laptop,
  DollarSign, RefreshCw, ShieldAlert, ChevronRight
} from 'lucide-react';
import { Button } from '../components/ui/button';

const starterWorkflows = [
  {
    workflow: 'Joiner onboarding',
    outcome: 'Accounts, groups, apps, devices provisioned',
    mode: 'Execute',
    approvalWhen: 'Admin roles or sensitive apps requested',
    verification: 'User active, groups assigned, apps accessible',
    icon: UserPlus
  },
  {
    workflow: 'Mover access change',
    outcome: 'Access updated to new role, old access revoked',
    mode: 'Propose',
    approvalWhen: 'Cross-department transfer or elevated access',
    verification: 'New access confirmed, old access removed',
    icon: ArrowLeftRight
  },
  {
    workflow: 'Leaver offboarding',
    outcome: 'Access revoked, devices wiped, data transferred',
    mode: 'Execute',
    approvalWhen: 'Executive or data custodian',
    verification: 'All access revoked, device compliance confirmed',
    icon: UserMinus
  },
  {
    workflow: 'Access request',
    outcome: 'App or resource access granted',
    mode: 'Propose',
    approvalWhen: 'Elevated privilege or sensitive resource',
    verification: 'Access functional, expiry set if applicable',
    icon: Key
  },
  {
    workflow: 'Password reset',
    outcome: 'Password reset, user notified',
    mode: 'Execute',
    approvalWhen: 'Service account or admin reset',
    verification: 'Login successful post-reset',
    icon: Lock
  },
  {
    workflow: 'MFA reset',
    outcome: 'MFA re-enrolled, verified',
    mode: 'Propose',
    approvalWhen: 'All MFA resets (identity verification required)',
    verification: 'MFA enrolled and tested',
    icon: Shield
  },
  {
    workflow: 'Software deployment',
    outcome: 'App deployed to device',
    mode: 'Execute',
    approvalWhen: 'Non-standard or licensed software',
    verification: 'App installed, license consumed',
    icon: Laptop
  },
  {
    workflow: 'License reclamation',
    outcome: 'Unused seat reclaimed',
    mode: 'Execute',
    approvalWhen: 'Active user seat or >10 seats',
    verification: 'Seat released, no access impact',
    icon: DollarSign
  },
  {
    workflow: 'Device compliance remediation',
    outcome: 'Device brought into compliance',
    mode: 'Propose',
    approvalWhen: 'Executive device or forced action',
    verification: 'Compliance status green',
    icon: RefreshCw
  },
  {
    workflow: 'Quarantine and containment',
    outcome: 'Device or account isolated',
    mode: 'Observe',
    approvalWhen: 'All containment actions (security review)',
    verification: 'Isolation confirmed, no lateral access',
    icon: ShieldAlert
  }
];

const autoExecute = [
  'Standard group membership assignment',
  'Pre-approved application access',
  'MDM profile deployment',
  'Inactive license reclamation',
  'Standard user password reset',
  'Non-destructive compliance remediation'
];

const approvalRequired = [
  'Admin role grants',
  'Sensitive app access (financial, HR, security)',
  'Device quarantine or wipe',
  'Shared account credential rotation',
  'Mailbox delegation',
  'Cross-department transfers'
];

const neverWithoutExplicit = [
  'Production infrastructure changes',
  'Network policy modifications',
  'Security control disablement',
  'Data deletion or export',
  'Bulk operations (>10 users)',
  'Break-glass actions'
];

export const Workflows = () => {
  return (
    <div className="min-h-screen bg-white" data-testid="workflows-page">
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-border">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-center justify-between h-14">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-6 h-6 rounded bg-primary flex items-center justify-center">
                <span className="text-primary-foreground font-bold text-xs">V</span>
              </div>
              <span className="text-sm font-semibold text-slate-900">Vedaya</span>
            </Link>
            <nav className="hidden md:flex items-center gap-6">
              <Link to="/workflows" className="text-sm font-medium text-primary">Workflows</Link>
              <Link to="/autonomy" className="text-sm text-muted-foreground hover:text-slate-900">Autonomy</Link>
              <Link to="/integrations" className="text-sm text-muted-foreground hover:text-slate-900">Integrations</Link>
              <Link to="/evidence" className="text-sm text-muted-foreground hover:text-slate-900">Evidence</Link>
              <Link to="/security-governance" className="text-sm text-muted-foreground hover:text-slate-900">Security</Link>
              <Link to="/pricing" className="text-sm text-muted-foreground hover:text-slate-900">Pricing</Link>
            </nav>
            <Button asChild size="sm">
              <Link to="/control-tower">Open Control Tower</Link>
            </Button>
          </div>
        </div>
      </header>

      <section className="py-10 px-6 border-b border-border">
        <div className="max-w-5xl mx-auto">
          <h1 className="text-2xl font-semibold text-slate-900 mb-2">Workflow Catalog</h1>
          <p className="text-slate-600">
            Pre-built workflows for identity lifecycle, access management, and IT operations.
            Each workflow defines execution mode, approval gates, and verification criteria.
          </p>
        </div>
      </section>

      <section className="py-10 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Available Workflows</h2>

          <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">Workflow</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">Outcome</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">Mode</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">Approval Required</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">Verification</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {starterWorkflows.map((wf, idx) => (
                    <tr key={idx} className="hover:bg-slate-50/50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <wf.icon className="w-4 h-4 text-slate-500" />
                          <span className="text-sm font-medium text-slate-900">{wf.workflow}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">{wf.outcome}</td>
                      <td className="px-4 py-3">
                        <span className={`badge-status ${
                          wf.mode === 'Observe' ? 'badge-neutral' :
                          wf.mode === 'Propose' ? 'badge-pending' :
                          'badge-active'
                        }`}>
                          {wf.mode}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">{wf.approvalWhen}</td>
                      <td className="px-4 py-3 text-sm text-slate-500">{wf.verification}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      <section className="py-10 px-6 bg-slate-50 border-y border-border">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Approval Policy Matrix</h2>

          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg border border-slate-200 p-4">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-6 h-6 rounded bg-primary/10 flex items-center justify-center">
                  <CheckCircle className="w-3.5 h-3.5 text-primary" />
                </div>
                <h3 className="text-sm font-semibold text-slate-900">Auto-Execute</h3>
              </div>
              <ul className="space-y-1.5">
                {autoExecute.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                    <span className="text-primary mt-1">•</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-white rounded-lg border border-slate-200 p-4">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-6 h-6 rounded bg-slate-200 flex items-center justify-center">
                  <AlertTriangle className="w-3.5 h-3.5 text-slate-600" />
                </div>
                <h3 className="text-sm font-semibold text-slate-900">Approval Required</h3>
              </div>
              <ul className="space-y-1.5">
                {approvalRequired.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                    <span className="text-slate-500 mt-1">•</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-white rounded-lg border border-slate-200 p-4">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-6 h-6 rounded bg-slate-900 flex items-center justify-center">
                  <Shield className="w-3.5 h-3.5 text-white" />
                </div>
                <h3 className="text-sm font-semibold text-slate-900">Explicit Enablement Only</h3>
              </div>
              <ul className="space-y-1.5">
                {neverWithoutExplicit.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                    <span className="text-slate-900 mt-1">•</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="py-10 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Autonomy Progression</h2>

          <div className="bg-white rounded-lg border border-slate-200 p-5">
            <div className="flex items-center gap-3 mb-5">
              {['Read-only', 'Propose', 'Execute', 'Full autonomy'].map((phase, idx) => (
                <React.Fragment key={phase}>
                  <div className="flex flex-col items-center">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold ${
                      idx === 0 ? 'bg-slate-100 text-slate-700' :
                      idx === 1 ? 'bg-slate-200 text-slate-700 border border-slate-300' :
                      idx === 2 ? 'bg-primary/10 text-primary border border-primary/20' :
                      'bg-primary text-white border border-primary'
                    }`}>
                      {idx + 1}
                    </div>
                    <span className="text-xs text-slate-600 mt-1.5">{phase}</span>
                  </div>
                  {idx < 3 && <ChevronRight className="w-4 h-4 text-slate-300" />}
                </React.Fragment>
              ))}
            </div>

            <div className="space-y-3 text-sm text-slate-700">
              <div className="flex gap-3">
                <span className="font-medium text-slate-900 w-16 shrink-0">Phase 1</span>
                <span>Read-only mode. System connects to data sources, baselines current state. No write operations.</span>
              </div>
              <div className="flex gap-3">
                <span className="font-medium text-slate-900 w-16 shrink-0">Phase 2</span>
                <span>Propose mode. System generates action bundles, routes for approval. All actions reviewed before execution.</span>
              </div>
              <div className="flex gap-3">
                <span className="font-medium text-slate-900 w-16 shrink-0">Phase 3</span>
                <span>Execute mode. Pre-approved action types execute automatically. Approval required for escalated actions.</span>
              </div>
              <div className="flex gap-3">
                <span className="font-medium text-slate-900 w-16 shrink-0">Phase 4</span>
                <span>Full autonomy. Per-workflow autonomy based on verification pass rate and rollback history.</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-10 px-6 bg-slate-50 border-t border-border">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-900 mb-1">Configure workflow policies</h2>
            <p className="text-sm text-slate-600">Set approval chains, execution modes, and verification gates per workflow.</p>
          </div>
          <div className="flex items-center gap-3">
            <Button asChild variant="outline">
              <Link to="/autonomy">View Autonomy Settings</Link>
            </Button>
            <Button asChild>
              <Link to="/control-tower">
                Open Control Tower
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <footer className="py-6 px-6 border-t border-border">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 rounded bg-primary flex items-center justify-center">
              <span className="text-primary-foreground font-bold text-2xs">V</span>
            </div>
            <span className="text-xs text-slate-500">Vedaya AI</span>
          </div>
          <p className="text-xs text-slate-400">© 2026 Vedaya AI Inc.</p>
        </div>
      </footer>
    </div>
  );
};

export default Workflows;
